import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "date", "preparation", "container", "section", "enabled", "timing", "timingFields", "customFields", "customDays", "deliveryDetails", "deliverySummary", "closingNotice"]
  static values = { hadReminder: Boolean, channelState: String, localDate: String, timingDays: Object }

  connect() {
    this.sync()
  }

  sync() {
    if (!this.hasStatusTarget || !this.hasSectionTarget) return

    const planned = this.statusTarget.value === "planned"
    const hasPlannedDate = !this.hasDateTarget || this.dateTarget.value !== ""
    const linkedPreparation = this.hasPreparationTarget && this.preparationTarget.value !== ""
    const eligible = planned && hasPlannedDate && !linkedPreparation
    const enabled = this.hasEnabledTarget && this.enabledTarget.checked
    const closesReminder = !planned || linkedPreparation

    this.sectionTarget.classList.toggle("hidden", !eligible)

    if (this.hasClosingNoticeTarget) {
      this.closingNoticeTarget.classList.toggle("hidden", !closesReminder || (!enabled && !this.hadReminderValue))
    }

    if (!eligible) return

    if (this.hasTimingFieldsTarget) {
      this.timingFieldsTarget.classList.toggle("hidden", !enabled)
    }

    if (this.hasCustomFieldsTarget) {
      const custom = enabled && this.hasTimingTarget && this.timingTarget.value === "custom"
      this.customFieldsTarget.classList.toggle("hidden", !custom)
    }

    this.syncDeliverySummary(enabled)
  }

  syncDeliverySummary(enabled) {
    if (!this.hasDeliveryDetailsTarget || !this.hasDeliverySummaryTarget) return

    this.deliveryDetailsTarget.classList.toggle("hidden", !enabled)
    if (!enabled) return

    const days = this.selectedAdvanceDays()
    const summary = this.deliverySummary(days)
    this.deliverySummaryTarget.textContent = summary || ""
    this.deliverySummaryTarget.classList.toggle("hidden", summary === null)
  }

  selectedAdvanceDays() {
    if (!this.hasTimingTarget) return null

    if (this.timingTarget.value === "custom") {
      if (!this.hasCustomDaysTarget || !/^\d+$/.test(this.customDaysTarget.value.trim())) return null

      const days = Number(this.customDaysTarget.value)
      return days <= 365 ? days : null
    }

    const days = this.timingDaysValue[this.timingTarget.value]
    return Number.isInteger(days) ? days : null
  }

  deliverySummary(days) {
    const inAppSummary = this.inAppSummary(days)

    switch (this.channelStateValue) {
      case "outside_release":
        return inAppSummary
      case "email_available":
        if (inAppSummary === null) return null
        if (this.hadReminderValue) return `${inAppSummary} Email is available for eligible planned-meal reminders you create or change.`
        if (!this.emailTimingOpen(days)) return `${inAppSummary} The selected email timing has already passed.`
        if (this.requestedLeadDatePassed(days)) return `${inAppSummary} A reminder email will also be sent soon.`
        if (this.reminderDue(days)) return `${inAppSummary} It will also be sent to you by email ${this.timingPhrase(days)}.`

        return `This reminder will appear in My Kitchen and be sent to you by email ${this.timingPhrase(days)}.`
      case "locally_stopped":
        return inAppSummary === null ? null : `${inAppSummary} Planned-meal reminder email is turned off.`
      case "current_address_suppressed":
        return inAppSummary === null ? null : `${inAppSummary} Reminder email cannot be sent to your current email address.`
      case "provider_dispatch_paused":
        return inAppSummary === null ? null : `${inAppSummary} Reminder email is temporarily unavailable.`
      default:
        return null
    }
  }

  inAppSummary(days) {
    if (days === null) return null
    if (this.reminderDue(days)) {
      return this.hadReminderValue
        ? "This reminder is available in My Kitchen now."
        : "This reminder will appear in My Kitchen when you save."
    }

    return `This reminder will appear in My Kitchen ${this.timingPhrase(days)}.`
  }

  reminderDue(days) {
    const dates = this.reminderAndLocalDateOrdinals(days)
    return dates !== null && dates.reminderDate <= dates.localDate
  }

  emailTimingOpen(days) {
    const dates = this.reminderAndLocalDateOrdinals(days)
    if (dates === null) return false

    return days > 0
      ? dates.relevantDate > dates.localDate
      : dates.relevantDate >= dates.localDate
  }

  requestedLeadDatePassed(days) {
    const dates = this.reminderAndLocalDateOrdinals(days)
    return dates !== null && dates.reminderDate < dates.localDate
  }

  reminderAndLocalDateOrdinals(days) {
    if (!this.hasDateTarget || !this.hasLocalDateValue) return null

    const relevantDate = this.isoDateOrdinal(this.dateTarget.value)
    const localDate = this.isoDateOrdinal(this.localDateValue)
    if (relevantDate === null || localDate === null) return null

    return { relevantDate, reminderDate: relevantDate - days, localDate }
  }

  isoDateOrdinal(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (!match) return null

    const date = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    return Number.isNaN(date) ? null : Math.floor(date / 86400000)
  }

  timingPhrase(days) {
    if (days === 0) return "on the planned date"
    if (days === 1) return "1 day before the planned date"

    return `${days} days before the planned date`
  }
}
